import React, { useContext, useEffect, useState, useMemo } from 'react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Label,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    Brush,
} from 'recharts'
import dayjs from 'dayjs'

import { WidgetDataContext } from '../../../wrappers/WidgetDataContext'

import { formatNumber } from '../../../../utils/numberFormatting'
import { dateToString } from '../../../../utils/dateFormatting'
import getHiddenColumns from '../../../../utils/widgets/getHiddenColumns'
import { ColorContext } from '../../../wrappers/ColorContext'
import withConfig from '../../../wrappers/withConfig'
import CustomLegend from './CustomLegend'
import { getReferenceLineData, getSeriesFromData } from './utils'

const defaultMaxTicks = 150

export default withConfig(({ config }) => {
    const { widgetData: widget, showLegend } = useContext(WidgetDataContext)
    const { colorMap, updateColorMap, getAssociatedColor } =
        useContext(ColorContext)
    const chartConfig =
        config && config['CHART'] && config['CHART'][widget.DashboardName]
    const maxTicks =
        chartConfig && chartConfig['MAX_TICKS']
            ? chartConfig['MAX_TICKS']
            : defaultMaxTicks
    const data = useMemo(() => (widget ? widget.Data : null), [widget])
    const options = useMemo(
        () => (widget ? widget.WidgetOptions : null),
        [widget]
    )
    const [legend, setLegend] = useState([])

    const referenceData = useMemo(
        () => getReferenceLineData(options),
        [options]
    )
    const preparedData = useMemo(
        () =>
            data
                .filter((d) => !!d.Date) // remove data w/out dates
                .map((d) => ({
                    ...d,
                    Date: dayjs(d.Date).toDate().getTime(),
                })), // transform dates to MM/DD/YYYY format
        [data]
    )

    const hiddenColumns = useMemo(() => getHiddenColumns(widget), [widget])
    const [chartData, uniqueGroups, groupKey] = useMemo(
        () => getSeriesFromData(preparedData, hiddenColumns),
        [preparedData, hiddenColumns]
    )
    
    // create state to track hidden prop for each individual line
    const [hiddenState, setHiddenState] = useState(
        uniqueGroups.reduce((acc, curr) => ({ ...acc, [curr]: false }), {})
    )
    const [bold, setBold] = useState(null)

    useEffect(() => {
        updateColorMap(groupKey, uniqueGroups)
    }, [uniqueGroups, groupKey])

    useEffect(() => {
        setLegend(
            uniqueGroups.map((groupName, idx) => ({
                color: !hiddenState[groupName]
                    ? getAssociatedColor(groupName, groupKey)
                    : 'rgb(204, 204, 204)',
                type: 'line',
                dataKey: groupName,
                value: groupName,
                hide: hiddenState[groupName] ? true : false,
            }))
        )
    }, [hiddenState, colorMap])

    useEffect(() => {
        if (showLegend === 'all' && Object.values(hiddenState).includes(true)) {
            setHiddenState(
                uniqueGroups.reduce(
                    (acc, curr) => ({ ...acc, [curr]: false }),
                    {}
                )
            )
        }
        if (
            showLegend === 'none' &&
            Object.values(hiddenState).includes(false)
        ) {
            setHiddenState(
                uniqueGroups.reduce(
                    (acc, curr) => ({ ...acc, [curr]: true }),
                    {}
                )
            )
        }
    }, [showLegend])

    const colors = useMemo(
        () =>
            uniqueGroups.reduce((acc, curr, idx) => {
                return {
                    ...acc,
                    [curr]: getAssociatedColor(curr, groupKey),
                }
            }, {}),
        [uniqueGroups, groupKey, colorMap]
    )

    const updateHiddenState = (e) => {
        setHiddenState((previousState) => ({
            ...previousState,
            [e.dataKey]: !previousState[e.dataKey],
        }))
    }

    const dataMax = useMemo(
        () =>
            Math.max(
                ...chartData.map((entry) =>
                    Math.max(
                        ...Object.keys(entry).map((key) =>
                            key !== 'Date' && !hiddenState[key] ? entry[key] : 0
                        )
                    )
                )
            ),
        [chartData, hiddenState]
    )

    const dataMin = useMemo(
        () =>
            Math.min(
                ...chartData.map((entry) =>
                    Math.min(
                        ...Object.keys(entry).map((key) =>
                            key !== 'Date' && !hiddenState[key]
                                ? entry[key]
                                : Infinity
                        )
                    )
                )
            ),
        [chartData, hiddenState]
    )

    // generate a line component for each
    // individual line
    const Lines = useMemo(
        () =>
            uniqueGroups.map((groupName, idx) => (
                <Line
                    type="monotone"
                    dot={false}
                    key={`bar-${idx}`}
                    dataKey={`${groupName}`}
                    stackId={'shared'}
                    stroke={colors[groupName]}
                    strokeWidth={bold === groupName ? 3 : 1}
                    activeDot={{ r: 5 }}
                    hide={hiddenState[groupName]}
                />
            )),
        [uniqueGroups, hiddenState, colors, bold]
    )

    const References = useMemo(
        () =>
            uniqueGroups.map((groupName, idx) => {
                const hasReference = referenceData
                    ? !!referenceData[groupName]
                    : false
                if (!hasReference) {
                    return null
                }
                return (
                    <ReferenceLine
                        y={referenceData[groupName]}
                        stroke={
                            hiddenState[groupName] ? 'none' : colors[groupName]
                        }
                        strokeWidth={bold === groupName ? 3 : 1}
                        strokeDasharray="10 1"
                        key={groupName}
                    />
                )
            }),
        [uniqueGroups, referenceData, hiddenState, colors, bold]
    )

    return (
        <ResponsiveContainer height={'99%'} debounce={1}>
            <LineChart
                data={chartData}
                margin={{
                    top: 20,
                    right: 30,
                    left: 10,
                    bottom: 20,
                }}
            >
                <Legend
                    content={
                        <CustomLegend
                            legendPayload={legend}
                            bold={bold}
                            setBold={setBold}
                            hiddenState={hiddenState}
                            updateHiddenState={updateHiddenState}
                        />
                    }
                    verticalAlign="top"
                    wrapperStyle={{
                        display: showLegend
                            ? Object.keys(legend).length
                                ? 'block'
                                : 'none'
                            : 'none',
                        top: 1,
                        left: 0,
                        overflowX: 'hidden',
                        overflowY: 'scroll',
                        alignItems: 'center',
                        width: '100%',
                    }}
                />
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    type="number"
                    domain={['dataMin', 'dataMax']}
                    dataKey="Date"
                    tickFormatter={(unixTime) => dateToString(unixTime)}
                >
                    <Label
                        value={'Date'}
                        offset={-10}
                        position="insideBottom"
                        className="barChartXAxisLabel"
                    />
                </XAxis>
                <YAxis
                    tickFormatter={(value) => formatNumber(value, 'compact')}
                    domain={[dataMin, dataMax]}
                    allowDataOverflow={true}
                >
                    <Label
                        value={widget.ValueLabel}
                        angle={-90}
                        offset={20}
                        position="insideLeft"
                        className="barChartYAxisLabel"
                    />
                </YAxis>
                <Tooltip
                    formatter={(value) => formatNumber(value)}
                    labelFormatter={(label) => dateToString(label)}
                    isAnimationActive={false}
                    wrapperStyle={
                        Object.values(hiddenState).filter((x) => x === false)
                            .length > 10
                            ? { fontSize: '.75rem' }
                            : { fontSize: '1rem' }
                    }
                    itemStyle={{ padding: '0px' }}
                />
                {Lines}
                {References}
                {chartData && chartData.length > maxTicks && (
                    <Brush
                        endIndex={maxTicks}
                        travellerWidth={0}
                        dataKey={'Date'}
                        height={20}
                        tickFormatter={(unixTime) => dateToString(unixTime)}
                    />
                )}
            </LineChart>
        </ResponsiveContainer>
    )
})
